import React from "react";

/**
 * Top-level React error boundary.
 *
 * Catches (a) render-time crashes and (b) failed lazy-chunk imports — the
 * latter is common right after a deploy invalidates the old hashed chunks,
 * which otherwise white-screens the whole SPA with no recovery. Shows a
 * reload CTA (the new chunks exist server-side, so a reload fixes it).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    const msg = `${error?.name ?? ""} ${error?.message ?? ""}`;
    const isChunkError =
      /ChunkLoadError|Loading chunk|dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i.test(
        msg
      );
    return { hasError: true, isChunkError };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("ErrorBoundary caught:", error, info);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { isChunkError } = this.state;

    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white px-6">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-6 w-6 text-gray-600"
              aria-hidden="true"
            >
              <path d="M23 4v6h-6" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </div>
          <h2 className="text-base font-bold text-gray-900">
            {isChunkError ? "A new version is available" : "Something went wrong"}
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            {isChunkError
              ? "Please reload to get the latest version of the app."
              : "An unexpected error occurred. Reloading the page usually fixes it."}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-5 w-full rounded-lg bg-gray-900 py-2.5 text-sm font-semibold text-white hover:bg-black"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
